"use client";

import { useState } from "react";
import type { FormEvent } from "react";
import { EMPTY_CONTACT_FORM } from "@/constants/contact.constants";
import { contactService } from "@/services";
import type { ContactFormErrors, ContactFormState, ContactFormTouched } from "@/types/contact.types";
function validate(form: ContactFormState): ContactFormErrors {
  const errs: ContactFormErrors = {};
  if (!form.name.trim()) errs.name = "Name is required";
  if (!form.email.trim()) errs.email = "Email is required";else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Looks malformed";
  if (!form.phone.trim()) errs.phone = "Phone is required";
  if (!form.category) errs.category = "Pick a project category";
  if (!form.budget) errs.budget = "Pick a budget range";
  if (!form.message.trim()) errs.message = "Project description is required";
  return errs;
}
export function useContactForm() {
  const [form, setForm] = useState<ContactFormState>(EMPTY_CONTACT_FORM);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [touched, setTouched] = useState<ContactFormTouched>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const setField = <K extends keyof ContactFormState,>(k: K, v: ContactFormState[K]) => {
    setForm(p => ({
      ...p,
      [k]: v
    }));
    if (touched[k]) {
      setErrors(validate({
        ...form,
        [k]: v
      }));
    }
  };
  const markTouched = (k_0: keyof ContactFormState) => setTouched(p_0 => ({
    ...p_0,
    [k_0]: true
  }));
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    setTouched({
      name: true,
      email: true,
      phone: true,
      category: true,
      budget: true,
      message: true,
      marketing_opt_in: true
    });
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    setServerError(null);
    try {
      await contactService.submit({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        category: form.category,
        budget: form.budget,
        message: form.message.trim(),
        marketing_opt_in: form.marketing_opt_in
      });
      setSubmitted(true);
    } catch {
      setServerError("Couldn't send your message right now. Email hello@cybitix.com directly and we'll respond.");
    } finally {
      setSubmitting(false);
    }
  };
  return {
    form,
    errors,
    touched,
    submitting,
    submitted,
    serverError,
    setField,
    markTouched,
    onSubmit
  };
}